// src/routes/merchants/picker/MerchantBrowser.tsx
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Skeleton } from '../../../platform/async/Skeleton'
import { Button } from '../../../platform/primitives/Button'
import { MerchantAvatar } from '../../../platform/primitives/MerchantAvatar'
import { SearchField } from '../../../platform/primitives/SearchField'
import { useHitTarget } from '../../../platform/responsive/hitTarget'
import { useBreakpoint } from '../../../platform/responsive/useBreakpoint'
import { hideOwned, type Brand, type PickExtras } from '../catalogue/catalogueModel'
import { RegionLabel } from '../catalogue/RegionLabel'
import type { BrandSearch } from '../catalogue/useCatalogue'
import { exactMatch, mergeMerchants, searchMerchants, type PickerMerchant } from './merchantPickerModel'
import type { DirectoryState } from './useMerchantPicker'
import './merchantPicker.css'

/** The server's own limit on a merchant name (`MerchantCreate.name`), so a typed query can always be created. */
const NAME_MAX = 255

/** What a create resolves to. A result, not a throw, so this component never reads a transport error type. */
export type CreateResult = { ok: true; merchant: PickerMerchant } | { ok: false; status: number | null }
export type CreateMerchant = (name: string) => Promise<CreateResult>

type Paged = { hasMore: boolean; loadingMore: boolean; loadMore: () => void }

/** The catalogue under the search results: the brand search for `term`, and the confirm step a brand opens. */
export type BrandOffer = BrandSearch & {
  term: string
  /** The caller's confirm step for `brand`; `cancel` closes it. */
  confirm: (brand: Brand, cancel: () => void) => ReactNode
}

export type MerchantBrowserProps = {
  value: string | null
  /** `extras` only from a brand's confirm step, when it also sets the transaction's category. */
  onPick: (merchant: PickerMerchant, extras?: PickExtras) => void
  /** The search box, owned by the caller: it debounces the value into `search`. */
  query: string
  onQueryChange: (q: string) => void
  mostUsed: readonly PickerMerchant[]
  /** "Most used" has not answered yet: its section shows a placeholder. Nothing else waits for it. */
  mostUsedLoading?: boolean
  /** The browse: every page loaded so far, in the server's order (the user's own A–Z, then shared A–Z). */
  directory: Paged & { merchants: readonly PickerMerchant[] }
  /** The server search, and the term it answers — which lags `query` by the debounce. */
  search: Paged & { state: DirectoryState; term: string }
  /** Present only when the user may create merchants (`custom_merchants`). Never rejects: a failure resolves with its HTTP status. */
  create: CreateMerchant | null
  /** The brand catalogue (#495), or `null` where it is not offered (no `custom_merchants`). */
  brands?: BrandOffer | null
  autoFocus?: boolean
}

/**
 * The merchant picker's body — Option B of the 2026-10-15 design: logo tiles for the usual ones, a list
 * for the rest. The category picker's structure (`CategoryBrowser`), without the rail: merchants have
 * no groups.
 *
 * **Tiles and rows are buttons with `aria-pressed`, not radios**, for `CategoryBrowser`'s reason: every
 * caller but bulk closes on a pick, and a radiogroup would pick on the first arrow key.
 *
 * **The list is the server's two runs, under two headings.** The server orders by `is_global, name`: the
 * user's own merchants A–Z, then the shared catalogue A–Z. Each run is drawn under its own heading, in the
 * order sent — never re-sorted here, which would move rows above the user's scroll position as pages land.
 *
 * **Search reaches the server.** The user may see more merchants than any page holds, so a query is
 * answered by the loaded pages at once and by the server's search once it lands. Every name match is
 * ranked together; a server hit whose name does not match (the server may match more than the name) is
 * kept, after them, in the server's order. Enter picks the top result.
 *
 * **Create is offered only when it cannot duplicate**: the caller allows it, the server has answered
 * for exactly this query with nothing left unread, and no result has this name in any case. A typed
 * "costco" finds Costco instead.
 *
 * **Brands come after merchants.** While searching, a "Brands" section lists catalogue brands for the
 * same term, each with its region and suggested category, less any brand a listed merchant already has
 * (`hideOwned`; the server hides the user's own already). Choosing one opens the caller's confirm step in
 * place of the results; nothing is added until that step's Add. Typing a new query closes it.
 *
 * Presentational: the caller (`MerchantBrowserLoader`) owns every request.
 */
export function MerchantBrowser({ value, onPick, query, onQueryChange, mostUsed, mostUsedLoading = false, directory, search, create, brands = null, autoFocus }: MerchantBrowserProps) {
  const phone = useBreakpoint() === 'phone'
  const target = useHitTarget('form')
  const q = query.trim()
  const searching = q !== ''
  const answered = search.term.trim() === q ? search.state : { status: 'loading' as const }
  const remote = answered.status === 'success' || answered.status === 'stale' ? answered.data.merchants : []
  const results = searching ? searchResults(mergeMerchants(mostUsed, directory.merchants, remote), remote, q) : []
  const offerCreate = create !== null && searching && answered.status === 'success' && !search.hasMore && exactMatch(results, q) === undefined

  const note = !searching
    ? ''
    : results.length > 0
      ? `${results.length} ${results.length === 1 ? 'merchant' : 'merchants'}`
      : answered.status === 'success'
        ? `No merchant called “${q}”`
        : answered.status === 'error'
          ? ''
          : 'Searching…'

  const pressed = (m: PickerMerchant) => m.id === value
  const label = (m: PickerMerchant) => (
    <>
      <span className="merchant-browser__name">{m.name}</span>
      {m.identity.is_global && <span className="sr-only">, shared</span>}
    </>
  )
  const avatar = (m: PickerMerchant, size: 'row' | 'detail') => (
    // Hidden: the logo's alt and its "Shared merchant" badge would repeat what the label says.
    <span aria-hidden="true" className="merchant-browser__avatar">
      <MerchantAvatar merchant={m.identity} size={size} />
    </span>
  )
  const rows = (list: readonly PickerMerchant[], listLabel?: string) => (
    <ul className="merchant-list" aria-label={listLabel}>
      {list.map((m) => (
        <li key={m.id}>
          <button type="button" className="merchant-row" data-merchant-id={m.id} style={{ minHeight: target }} aria-pressed={pressed(m)} onClick={() => onPick(m)}>
            {avatar(m, 'row')}
            {label(m)}
          </button>
        </li>
      ))}
    </ul>
  )

  // "Show more" → focus the first merchant of the new page; the button itself may be gone (IconCataloguePicker).
  // Found by id, not by position: the two runs draw a new page's own merchants above the shared ones.
  const scroll = useRef<HTMLDivElement>(null)
  const focusFrom = useRef<number | null>(null)
  const { merchants: listed } = directory
  const count = listed.length
  useEffect(() => {
    const from = focusFrom.current
    if (from === null || count <= from) return
    focusFrom.current = null
    const first = listed[from]
    if (first === undefined) return
    const rows = scroll.current?.querySelectorAll<HTMLButtonElement>('.merchant-browser__run .merchant-row') ?? []
    Array.from(rows).find((r) => r.dataset.merchantId === first.id)?.focus()
  }, [count, listed])

  // Scrolling to the end of the A–Z list asks for the next page. The button is the keyboard path.
  const sentinel = useRef<HTMLDivElement>(null)
  const { hasMore, loadMore } = directory
  useEffect(() => {
    const el = sentinel.current
    if (el === null || !hasMore || searching || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && loadMore(), {
      root: scroll.current,
      rootMargin: '120px',
    })
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore, loadMore, searching])

  const brandList =
    brands !== null && searching && brands.term.trim() === q && brands.status === 'success'
      ? hideOwned(brands.brands, mergeMerchants(mostUsed, directory.merchants, remote).map((m) => m.name))
      : []
  // The brand being confirmed, for the query it was chosen under: a new query closes the step.
  const [confirming, setConfirming] = useState<{ brand: Brand; q: string } | null>(null)
  const confirmed = brands !== null && confirming !== null && confirming.q === q ? confirming.brand : null
  const brandsId = useId()
  // Cancel returns focus to the brand row the step was opened from, once the closed step uncovers it.
  const refocusBrand = useRef<string | null>(null)
  useEffect(() => {
    const id = refocusBrand.current
    if (confirmed !== null || id === null) return
    refocusBrand.current = null
    const rows = scroll.current?.querySelectorAll<HTMLButtonElement>('[data-brand-id]') ?? []
    Array.from(rows).find((r) => r.dataset.brandId === id)?.focus()
  }, [confirmed])

  const mostId = useId()
  const ownId = useId()
  const sharedId = useId()
  const own = listed.filter((m) => !m.identity.is_global)
  const shared = listed.filter((m) => m.identity.is_global)

  return (
    <div
      className="merchant-browser"
      onKeyDown={(e) => {
        if (e.key !== 'Enter' || !(e.target instanceof HTMLInputElement) || e.target.type !== 'search') return
        e.preventDefault()
        const top = results[0]
        if (top !== undefined) onPick(top)
      }}
    >
      <SearchField label="Search merchants" placeholder="Search merchants" value={query} onChange={onQueryChange} maxLength={NAME_MAX} autoFocus={autoFocus} />

      {/* Mounted before it has text, so a change is announced. */}
      <p role="status" className="merchant-browser__note">
        {note}
      </p>

      <div className="merchant-browser__scroll" ref={scroll}>
        {confirmed !== null && brands !== null ? (
          brands.confirm(confirmed, () => {
            refocusBrand.current = confirmed.id
            setConfirming(null)
          })
        ) : searching ? (
          <>
            {results.length > 0 && rows(results, 'Search results')}
            {answered.status === 'error' && (
              <div className="merchant-browser__empty">
                <span>{answered.message}</span>
                {answered.recovery !== undefined && (
                  <Button type="button" variant="chip" size="form" onClick={answered.recovery.onAct}>
                    {answered.recovery.label}
                  </Button>
                )}
              </div>
            )}
            {search.hasMore && search.term.trim() === q && (
              <MoreButton loading={search.loadingMore} onClick={search.loadMore}>
                Show more results
              </MoreButton>
            )}
            {brandList.length > 0 && (
              <section className="merchant-browser__section" aria-labelledby={brandsId}>
                <h3 id={brandsId} className="merchant-browser__heading">
                  Brands
                </h3>
                <ul className="merchant-list">
                  {brandList.map((b) => (
                    <li key={b.id}>
                      <button type="button" className="merchant-row" data-brand-id={b.id} style={{ minHeight: target }} onClick={() => setConfirming({ brand: b, q })}>
                        <span className="merchant-browser__name">{b.name}</span>
                        <RegionLabel region={b.region} />
                        {b.suggestedCategory !== null && <span className="brand-row__category">{b.suggestedCategory.name}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {offerCreate && <CreateOffer name={q} create={create} onCreated={(m) => onPick(m)} />}
            {results.length === 0 && answered.status === 'success' && (
              <div className="merchant-browser__empty">
                <Button type="button" variant="chip" size="form" onClick={() => onQueryChange('')}>
                  Clear search
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            {mostUsedLoading ? (
              // Its own placeholder, one tile-row tall: the list below is already usable.
              <section className="merchant-browser__section" aria-labelledby={mostId} aria-busy="true">
                <h3 id={mostId} className="merchant-browser__heading">
                  Most used
                </h3>
                <Skeleton height={88} />
              </section>
            ) : (
              mostUsed.length > 0 && (
                <section className="merchant-browser__section" aria-labelledby={mostId}>
                  <h3 id={mostId} className="merchant-browser__heading">
                    Most used
                  </h3>
                  <div className={phone ? 'merchant-tiles merchant-tiles--phone' : 'merchant-tiles'}>
                    {mostUsed.map((m) => (
                      <button key={m.id} type="button" className="merchant-tile" style={{ minHeight: target }} aria-pressed={pressed(m)} onClick={() => onPick(m)}>
                        {avatar(m, 'detail')}
                        {label(m)}
                      </button>
                    ))}
                  </div>
                </section>
              )
            )}
            {listed.length === 0 && <p className="merchant-browser__empty">No merchants yet.</p>}
            {own.length > 0 && (
              <section className="merchant-browser__section merchant-browser__run" aria-labelledby={ownId}>
                <h3 id={ownId} className="merchant-browser__heading">
                  Your merchants
                </h3>
                {rows(own)}
              </section>
            )}
            {shared.length > 0 && (
              <section className="merchant-browser__section merchant-browser__run" aria-labelledby={sharedId}>
                <h3 id={sharedId} className="merchant-browser__heading">
                  Shared catalogue
                </h3>
                {rows(shared)}
              </section>
            )}
            {hasMore && (
              <>
                <div ref={sentinel} className="merchant-browser__sentinel" aria-hidden="true" />
                <MoreButton
                  loading={directory.loadingMore}
                  onClick={() => {
                    focusFrom.current = count
                    loadMore()
                  }}
                >
                  Show more merchants
                </MoreButton>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Search results: every merchant whose NAME matches, ranked (exact, prefix, word-prefix, contains), then
 * the server's remaining hits in the order it sent them. The server's `search` may match more than the
 * name, and a hit it returned is never dropped for failing a client-side test it was not answering.
 */
function searchResults(pool: readonly PickerMerchant[], remote: readonly PickerMerchant[], q: string): PickerMerchant[] {
  const ranked = searchMerchants(pool, q)
  const seen = new Set(ranked.map((m) => m.id))
  return [...ranked, ...remote.filter((m) => !seen.has(m.id))]
}

/** The accessible name stays the same while loading, so focus and the user's place survive the wait. */
function MoreButton({ loading, onClick, children }: { loading: boolean; onClick: () => void; children: string }) {
  return (
    <Button type="button" variant="chip" size="form" aria-disabled={loading || undefined} onClick={() => !loading && onClick()}>
      {children}
    </Button>
  )
}

/** 400 is the server's duplicate-name answer (MerchantForm reads it the same way). */
function createFailure(status: number | null, name: string): string {
  if (status === 400) return `A merchant called “${name}” already exists.`
  return `Couldn’t create “${name}”. Try again.`
}

function CreateOffer({
  name,
  create,
  onCreated,
}: {
  name: string
  create: CreateMerchant
  onCreated: (m: PickerMerchant) => void
}) {
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<{ name: string; copy: string } | null>(null)
  const run = async () => {
    if (busy) return
    setBusy(true)
    setFailure(null)
    const result = await create(name)
    setBusy(false)
    if (result.ok) onCreated(result.merchant)
    else setFailure({ name, copy: createFailure(result.status, name) })
  }
  return (
    <div className="merchant-create">
      {/* The Button primitive, so its `aria-disabled` skin is the platform's (route sheets never skin a disabled state). */}
      <Button type="button" variant="outline" size="form" aria-disabled={busy || undefined} onClick={() => void run()}>
        {`Create “${name}”`}
      </Button>
      {/* A failure for a name the user has since changed is not shown against the new one. */}
      {failure !== null && failure.name === name && (
        <p role="alert" className="merchant-create__error">
          {failure.copy}
        </p>
      )}
    </div>
  )
}
